import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import styles from "./ObjectEditor.styles";
import ObjectGeneralFieldsBlock from "./object-editor-blocks/ObjectGeneralFieldsBlock";
import ObjectPlotBlock from "./object-editor-blocks/ObjectPlotBlock";
import ObjectAddressBlock, { ObjectAddressBlockHandle } from "./object-editor-blocks/ObjectAddressBlock";
import ObjectUnitsBlock, { ObjectUnitsBlockHandle } from "./object-editor-blocks/ObjectUnitsBlock";
import { kadIdValidator, objectFormValidator } from "../helpers/validator";
import AlertService from "../../services/notification.service";
import { createNewObjectRequest } from "../../../modules/add-new-entity/actions";
import { connect, useDispatch } from "react-redux";
import { selectLeadgen } from "../../../modules/leadgen/selectors";
import { updateObjectRequest } from "../../../modules/leadgen/actions/objects";
import DefaultButton from "../button/Button";
import type { RootState } from "../../../reducers";
import type { TypeEdit, Id } from  "../../../../types/editor.types";
import type { UpdateUnitsItem, Unit } from "../../../../types/object-editor.types";
import { isUnitsEditPayload } from './object-editor.guards';

type ObjectEditorProps = {
  typeEdit: TypeEdit | null;
  currentObject: any;
  objectId: Id | null;
  entityId: Id | null;
  closeModal: () => void;
}

const initialErrorsState = {
  isFilledKadId: true,
  isFilledCityCode: true,
  isFilledPlotNumber: true,
  isFilledSection: true,
  isFilledPlotIndex: true,
  isFilledCity: true,
};

const initialTouchedFieldsState = {
  kadaster_id: false,
  kadaster_url: false,
  price: false,
  restrictions: false,
}

const ObjectEditor: React.FC<ObjectEditorProps> = (props) => {

  const { typeEdit, currentObject, objectId, entityId, closeModal } = props;
  const { plot } = currentObject;
  const initialObjectState = {
    general_fields: {
      kadaster_id: "",
      kadaster_url: "",
      price: "",
      restrictions: "",
    },
    plot: {
      city_code: "",
      section: "",
      plot_number: "",
      index: "",
    },
    address: {
      id: null,
      city_id: "",
      city_name: "",
      street_name: "",
      number: "",
      number_add: "",
      postcode: "",
    },
    units: [] as Unit[],
    unitsForDelete: [] as Unit[],
  };
  const [objectForSave, setObjectForSave] = useState(initialObjectState);
  const [errors, setErrors] = useState(initialErrorsState);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errorFields, setErrorFields] = useState(initialTouchedFieldsState);
  const [actionTypes, setActionTypes] = useState<Array<string>>([]);
  const [canEditPrice, setCanEditPrice] = useState(true);
  const objectUnitsRef = useRef<ObjectUnitsBlockHandle | null>(null);
  const objectAddressRef = useRef<ObjectAddressBlockHandle | null>(null);

    useEffect(() => {
    if (typeEdit === "objectEdit" && Object.keys(currentObject).length > 0) {
      setObjectForSave({
        ...objectForSave,
        general_fields: {
          ...objectForSave.general_fields,
          kadaster_id: currentObject.kadaster_id || "",
          kadaster_url: currentObject.kadaster_url || "",
          price: currentObject.price || "",
          restrictions: currentObject.restrictions || "",
        },
        plot: {
          ...objectForSave.plot,
          city_code: plot.city_code || "",
          section: plot.section || "",
          plot_number: plot.plot_number || "",
          index: plot.index || "",
        },
        address: {
          ...objectForSave.address,
          ...(currentObject.objAddress && {
              id: currentObject?.objAddress?.id || null,
              city_id: currentObject.object_city.city_id,
              city_name: currentObject.object_city.city_name,
              street_name: currentObject.objAddress.street_name || "",
              number: currentObject.objAddress.house_number || "",
              number_add: currentObject.objAddress.number_add || "",
              postcode: currentObject.objAddress.postcode || "",
          }),
        },
        units: currentObject.units || [],
      });
      // setObjectId(currentObject.object_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeEdit, currentObject, plot]);

  useEffect(() => {
    if (objectForSave.units.length > 0) {
      setCanEditPrice(!objectForSave.units.some( (unit: any) => Number(unit?.price) > 0 )) ;
    } else {
      setCanEditPrice(true);
    }
  }, [objectForSave.units]);

  const handleChange = (
    name: string,
    key: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (name === "generalFields") {

      if (event.target.value && key === "kadaster_id") {
        setErrors({ ...errors, isFilledKadId: true });
      }
      if (event.target.value === "" && key === "kadaster_id") {
        setErrors({ ...errors, isFilledKadId: false });
      }
      if (!actionTypes.includes("general_fields")) {
        setActionTypes([...actionTypes, "general_fields"]);
      }
      return setObjectForSave({
        ...objectForSave,
        general_fields: {
          ...objectForSave.general_fields,
          [key]: event.target.value,
        },
      });
    }
    if (name === "objectPlot") {
      if (key === "city_code") {
        if (event.target.value) {
          setErrors({ ...errors, isFilledCityCode: true });
        } else {
          setErrors({ ...errors, isFilledCityCode: false });
        }
      }
      if (key === "section") {
        if (event.target.value) {
          setErrors({ ...errors, isFilledSection: true });
        } else {
          setErrors({ ...errors, isFilledSection: false });
        }
      }
      if (key === "plot_number") {
        if (event.target.value) {
          setErrors({ ...errors, isFilledPlotNumber: true });
        } else {
          setErrors({ ...errors, isFilledPlotNumber: false });
        }
      }
      if (key === "index") {
        if (event.target.value) {
          setErrors({ ...errors, isFilledPlotIndex: true });
        } else {
          setErrors({ ...errors, isFilledPlotIndex: false });
        }
      }
      if (!actionTypes.includes("plot")) {
        setActionTypes([...actionTypes, "plot"]);
      }

      return setObjectForSave({
        ...objectForSave,
        plot: {
          ...objectForSave.plot,
          [key]: event.target.value,
        },
      });
    }
    if (name === "objectAddress") {
      if (key === "city_name") {
        if (event.target.value) {
          setErrors({ ...errors, isFilledCity: true });
        } else {
          setErrors({ ...errors, isFilledCity: false });
        }
      }
      if (!actionTypes.includes("address")) {
        setActionTypes([...actionTypes, "address"]);
      }
      return setObjectForSave({
        ...objectForSave,
        address: {
          ...objectForSave.address,
          [key]: event.target.value,
        },
      });
    }
  };

  const updateUnits = (types: string[], item: UpdateUnitsItem, editMode: boolean) => {
    setActionTypes((prev) => [...prev, ...types]);
    if (isUnitsEditPayload(item)) {
      if (
        item.newStateForDelete.length &&
        !actionTypes.includes("units")
      ) {
        setActionTypes((prev) => [...prev, "units"]);
      }
      if (editMode) {
        return setObjectForSave((prev) =>
          ({
            ...prev,
            units: [...item.filteredList],
            unitsForDelete: [...prev.unitsForDelete, ...item.newStateForDelete],
         })
        );
      }
    }
    if (Array.isArray(item)) {
      return setObjectForSave((prev)  => ({
        ...prev,
        units: [...item],
      }));
    }
  };

  const handleSave = async () => {
    let isExistedKadId: any;
    if (objectForSave.general_fields.kadaster_id) {
      isExistedKadId = await kadIdValidator("object", objectForSave.general_fields.kadaster_id);
      if ((isExistedKadId.data && !objectId) || (isExistedKadId.data && objectId && isExistedKadId.data.id !== objectId)) {
        setErrorFields?.(prev => ({...prev, kadaster_id: true}));
        return AlertService.warning("Object with this KAD ID already exists");
      } else {
        setErrorFields?.(prev => ({...prev, kadaster_id: false}));
      }
    }
    const isValidObject = objectFormValidator(objectForSave);
    setErrors(isValidObject);
    if (
      Object.keys(isValidObject).every((item) => isValidObject[item] === true)
    ) {
      if (typeEdit !== "objectEdit") {
        if (objectForSave.address &&!objectForSave.address?.id) {
           delete (objectForSave.address as any).id;
        }
        dispatch(createNewObjectRequest(objectForSave, navigate));
      }
      if (typeEdit === "objectEdit") {
        // @ts-ignore
        const uniqueTypes = [...new Set(actionTypes)];
        dispatch(
          updateObjectRequest(uniqueTypes, objectId, objectForSave, entityId ? entityId : null)
        );
        // console.log('Object edit updated successfully');
        closeModal();
      }
    } else {
      AlertService.error("Fields are not valid");
    }
  };

  const handleCancel = () => {
    setErrors(initialErrorsState);
    setObjectForSave({
      general_fields: {
        kadaster_id: "",
        kadaster_url: "",
        price: "",
        restrictions: "",
      },
      plot: {
        city_code: "",
        section: "",
        plot_number: "",
        index: "",
      },
      address: {
        id: null,
        city_id: "",
        city_name: "",
        street_name: "",
        number: "",
        number_add: "",
        postcode: "",
      },
      units: [],
      unitsForDelete: [],
    });
    objectUnitsRef.current?.reset();
    objectAddressRef.current?.reset();
  };

  return (
    <Box sx={styles.root}>
      <ObjectGeneralFieldsBlock
        onChange={handleChange}
        objectFields={objectForSave.general_fields}
        errors={errors}
        canEditPrice={canEditPrice}
        errorFields={errorFields}
        setErrorFields={setErrorFields}

      />
      <ObjectPlotBlock
        onChange={handleChange}
        objectFields={objectForSave.plot}
        errors={errors}
      />
      <ObjectAddressBlock
        ref={objectAddressRef}
        onChange={handleChange}
        objectFields={objectForSave}
        // objectFields={objectForSave.address}
        unitsLength={objectForSave.units.length}
        errors={errors}
        setErrors={setErrors}
        selectAddressObject={setObjectForSave}
      />
      <ObjectUnitsBlock
        ref={objectUnitsRef}
        onChange={updateUnits}
        units={objectForSave.units}
      />
      <Box sx={styles.buttonHolder}>
        <DefaultButton
          variant="contained"
          styleType="save"
          handleClick={handleSave}
        >
          Save
        </DefaultButton>
        <DefaultButton
          variant="contained"
          styleType="cancel"
          onMouseDown={() => objectUnitsRef.current?.reset()}
          handleClick={
            typeEdit === "objectEdit" ? closeModal : handleCancel
          }
        >
          Cancel
        </DefaultButton>
      </Box>
    </Box>
  );
};

function mapStateToProps(state: RootState) {
  const { currentObject } = selectLeadgen(state);
  return { currentObject };
}

export default connect(mapStateToProps)(ObjectEditor);
